<template>
  <div v-if="showModal" class="profile-completion-overlay" @click.self="handleBackdropClick">
    <div class="profile-completion-modal">
      <div class="modal-header">
        <h3>Complete Your Profile</h3>
        <p class="modal-subtitle">
          To access nutrition features, please provide the following information:
        </p>
      </div>

      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: completionPercentage + '%' }"></div>
        <span class="progress-text">{{ completionPercentage }}% Complete</span>
      </div>

      <div class="fields-container">
        <!-- Height Field -->
        <div class="field-group" :class="{ completed: hasValidHeight }">
          <div class="field-header">
            <span class="field-icon">
              <span v-if="hasValidHeight" class="check-icon">&#10003;</span>
              <span v-else class="pending-icon">1</span>
            </span>
            <label>Height (cm)</label>
          </div>
          <div v-if="!hasValidHeight" class="field-input">
            <input type="number" v-model.number="formData.height" placeholder="Enter your height in cm" min="100"
              max="250" step="0.1" :disabled="saving" />
            <button class="save-btn" @click="saveHeight" :disabled="!isValidHeight || saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
          <div v-else class="field-value">
            {{ formData.height }} cm
            <button class="edit-btn" @click="editField('height')">Edit</button>
          </div>
        </div>

        <!-- Weight Field -->
        <div class="field-group" :class="{ completed: hasValidWeight }">
          <div class="field-header">
            <span class="field-icon">
              <span v-if="hasValidWeight" class="check-icon">&#10003;</span>
              <span v-else class="pending-icon">2</span>
            </span>
            <label>Weight (kg)</label>
          </div>
          <div v-if="!hasValidWeight" class="field-input">
            <input type="number" v-model.number="formData.weight" placeholder="Enter your weight in kg" min="30"
              max="200" step="0.1" :disabled="saving" />
            <button class="save-btn" @click="saveWeight" :disabled="!isValidWeight || saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
          <div v-else class="field-value">
            {{ formData.weight }} kg
            <button class="edit-btn" @click="editField('weight')">Edit</button>
          </div>
        </div>

        <!-- DOB Field -->
        <div class="field-group" :class="{ completed: hasValidDob }">
          <div class="field-header">
            <span class="field-icon">
              <span v-if="hasValidDob" class="check-icon">&#10003;</span>
              <span v-else class="pending-icon">3</span>
            </span>
            <label>Date of Birth</label>
          </div>
          <div v-if="!hasValidDob" class="field-input">
            <VueDatePicker :locale="'en-GB'" v-model="formData.dob" :enable-time-picker="false" :max-date="maxDobDate"
              :min-date="minDobDate" format="dd/MM/yyyy" placeholder="Select your date of birth" :disabled="saving"
              auto-apply />
            <button class="save-btn" @click="saveDob" :disabled="!isValidDob || saving">
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
          </div>
          <div v-else class="field-value">
            {{ formatDob(formData.dob) }}
            <button class="edit-btn" @click="editField('dob')">Edit</button>
          </div>
        </div>
      </div>

      <!-- Info Box -->
      <div class="info-box">
        <span class="info-icon">i</span>
        <p>
          This information is used to calculate your personalized nutrition targets
          based on your body weight. Your data is kept private and secure.
        </p>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <button v-if="allowSkip" class="skip-btn" @click="handleSkip" :disabled="saving">
          Skip for now
        </button>
        <button class="continue-btn" :disabled="!isComplete || saving" @click="handleComplete">
          Continue to Nutrition
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { Users } from "@/services/user-service";
import { mapGetters } from "vuex";

export default {
  name: "ProfileCompletionCheck",

  components: {
    VueDatePicker,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    allowSkip: {
      type: Boolean,
      default: false,
    },
    allowBackdropClose: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["complete", "skip", "close"],

  data() {
    return {
      showModal: false,
      saving: false,
      formData: {
        height: null,
        weight: null,
        dob: null,
      },
      editingFields: {
        height: false,
        weight: false,
        dob: false,
      },
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    userId() {
      return this.getProfile?.id;
    },

    // Check if current stored values are valid
    hasValidHeight() {
      const height = parseFloat(this.formData.height);
      return !isNaN(height) && height > 0 && !this.editingFields.height;
    },

    hasValidWeight() {
      const weight = parseFloat(this.formData.weight);
      return !isNaN(weight) && weight > 0 && !this.editingFields.weight;
    },

    hasValidDob() {
      const dob = this.formData.dob;
      if (!dob || dob === "0000-00-00" || this.editingFields.dob) {
        return false;
      }
      const date = new Date(dob);
      return !isNaN(date.getTime());
    },

    // Validation for form inputs
    isValidHeight() {
      const h = this.formData.height;
      return h && h >= 100 && h <= 250;
    },

    isValidWeight() {
      const w = this.formData.weight;
      return w && w >= 30 && w <= 200;
    },

    isValidDob() {
      return this.formData.dob && new Date(this.formData.dob) instanceof Date;
    },

    isComplete() {
      return this.hasValidHeight && this.hasValidWeight && this.hasValidDob;
    },

    completionPercentage() {
      let complete = 0;
      if (this.hasValidHeight) complete++;
      if (this.hasValidWeight) complete++;
      if (this.hasValidDob) complete++;
      return Math.round((complete / 3) * 100);
    },

    maxDobDate() {
      // Max DOB is today minus 10 years (minimum age for sports app)
      const date = new Date();
      date.setFullYear(date.getFullYear() - 10);
      return date;
    },

    minDobDate() {
      // Min DOB is 60 years ago
      const date = new Date();
      date.setFullYear(date.getFullYear() - 60);
      return date;
    },
  },

  watch: {
    show: {
      immediate: true,
      handler(val) {
        this.showModal = val;
        if (val) {
          this.loadProfileData();
        }
      },
    },
  },

  methods: {
    loadProfileData() {
      if (this.getProfile) {
        const profile = this.getProfile;
        this.formData.height = parseFloat(profile.height) || null;
        this.formData.weight = parseFloat(profile.weight) || null;
        this.formData.dob = profile.dob && profile.dob !== "0000-00-00" ? profile.dob : null;

        // Reset editing states
        this.editingFields = {
          height: false,
          weight: false,
          dob: false,
        };
      }
    },

    editField(field) {
      this.editingFields[field] = true;
    },

    async saveHeight() {
      if (!this.isValidHeight || !this.userId) return;

      this.saving = true;
      try {
        const userData = {
          id: this.userId,
          value: this.formData.height,
          action: "saveHeight",
        };
        const response = await Users.find(userData);
        if (response.status === 200) {
          this.editingFields.height = false;
          this.$toast.success("Height saved successfully");
          await this.refreshProfile();
        }
      } catch (error) {
        console.error("Error saving height:", error);
        this.$toast.error("Failed to save height");
      } finally {
        this.saving = false;
      }
    },

    async saveWeight() {
      if (!this.isValidWeight || !this.userId) return;

      this.saving = true;
      try {
        const userData = {
          id: this.userId,
          value: this.formData.weight,
          action: "saveWeight",
        };
        const response = await Users.find(userData);
        if (response.status === 200) {
          this.editingFields.weight = false;
          this.$toast.success("Weight saved successfully");
          await this.refreshProfile();
        }
      } catch (error) {
        console.error("Error saving weight:", error);
        this.$toast.error("Failed to save weight");
      } finally {
        this.saving = false;
      }
    },

    async saveDob() {
      if (!this.isValidDob || !this.userId) return;

      this.saving = true;
      try {
        // Format date for database
        const dobDate = new Date(this.formData.dob);
        const formattedDob = dobDate.toISOString().split("T")[0];

        const userData = {
          id: this.userId,
          dob: formattedDob,
          action: "saveDob",
        };
        const response = await Users.find(userData);
        if (response.status === 200) {
          this.editingFields.dob = false;
          this.$toast.success("Date of birth saved successfully");
          await this.refreshProfile();
        }
      } catch (error) {
        console.error("Error saving DOB:", error);
        this.$toast.error("Failed to save date of birth");
      } finally {
        this.saving = false;
      }
    },

    async refreshProfile() {
      // Refresh the profile in Vuex store
      try {
        await this.$store.dispatch("fetchProfile");
        this.loadProfileData();
      } catch (error) {
        console.error("Error refreshing profile:", error);
      }
    },

    formatDob(dob) {
      if (!dob) return "";
      const date = new Date(dob);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    handleComplete() {
      if (this.isComplete) {
        this.$emit("complete");
        this.showModal = false;
      }
    },

    handleSkip() {
      this.$emit("skip");
      this.showModal = false;
    },

    handleBackdropClick() {
      if (this.allowBackdropClose) {
        this.$emit("close");
        this.showModal = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.profile-completion-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.profile-completion-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  padding: 24px 24px 16px;
  text-align: center;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0 0 8px;
    font-size: 1.5rem;
    color: #333;
  }

  .modal-subtitle {
    margin: 0;
    color: #666;
    font-size: 0.9rem;
  }
}

.progress-bar {
  margin: 16px 24px;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #28a745, #20c997);
    border-radius: 4px;
    transition: width 0.3s ease;
  }

  .progress-text {
    position: absolute;
    right: 0;
    top: 12px;
    font-size: 0.75rem;
    color: #666;
  }
}

.fields-container {
  padding: 8px 24px;
}

.field-group {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 2px solid transparent;
  transition: all 0.2s ease;

  &.completed {
    background: #e8f5e9;
    border-color: #28a745;
  }
}

.field-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  label {
    font-weight: 600;
    color: #333;
    font-size: 0.95rem;
  }
}

.field-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;

  .check-icon {
    background: #28a745;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .pending-icon {
    background: #007bff;
    color: white;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.field-input {
  display: flex;
  gap: 8px;

  input {
    flex: 1;
    padding: 10px 14px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 1rem;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #007bff;
    }

    &:disabled {
      background: #e9ecef;
    }
  }

  .save-btn {
    padding: 10px 20px;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #0056b3;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
}

.field-value {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
  color: #333;

  .edit-btn {
    padding: 4px 12px;
    background: transparent;
    border: 1px solid #666;
    border-radius: 4px;
    color: #666;
    font-size: 0.8rem;
    cursor: pointer;

    &:hover {
      background: #f0f0f0;
    }
  }
}

.info-box {
  margin: 16px 24px;
  padding: 12px 16px;
  background: #e7f1ff;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  align-items: flex-start;

  .info-icon {
    width: 20px;
    height: 20px;
    background: #007bff;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    flex-shrink: 0;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #333;
    line-height: 1.4;
  }
}

.modal-actions {
  padding: 16px 24px 24px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #eee;

  .skip-btn {
    padding: 12px 24px;
    background: transparent;
    border: 1px solid #666;
    border-radius: 6px;
    color: #666;
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
      background: #f0f0f0;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .continue-btn {
    padding: 12px 24px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #1e7e34;
    }

    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  }
}

/* Date picker styling */
:deep(.dp__input) {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .profile-completion-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .profile-completion-modal {
    max-width: 100%;
    border-radius: 16px 16px 0 0;
    max-height: 95vh;
  }

  .modal-header {
    padding: 20px 16px 12px;

    h3 {
      font-size: 1.25rem;
    }
  }

  .progress-bar {
    margin: 12px 16px;
  }

  .fields-container {
    padding: 8px 16px;
  }

  .field-input {
    flex-direction: column;

    .save-btn {
      width: 100%;
    }
  }

  .info-box {
    margin: 12px 16px;
  }

  .modal-actions {
    padding: 12px 16px 20px;
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
}
</style>
